/**
 * Tour Launcher
 * Menu for browsing and starting available tours
 */

import { HelpCircle, Play, CheckCircle2, RotateCcw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useAuth } from '@/hooks/useAuth';
import { getAllTours } from '@/components/onboarding/tours';
import type { TourId } from '@/types/onboarding';

interface TourLauncherProps {
  /** Trigger element variant */
  variant?: 'icon' | 'button';
}

export function TourLauncher({ variant = 'icon' }: TourLauncherProps) {
  const { state, startTour, resetOnboarding } = useOnboarding();
  const { isAuthenticated } = useAuth();
  const allTours = getAllTours();

  // Filter out alerts tour for unauthenticated users (requires login to access notification settings)
  const tours = allTours.filter((tour) => tour.id !== 'alerts' || isAuthenticated);

  const isTourCompleted = (tourId: TourId) => state.completedTours.includes(tourId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'icon' ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
            aria-label="Help and Tours"
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        ) : (
          <Button variant="outline" className="gap-2">
            <HelpCircle className="h-4 w-4" />
            Help
          </Button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Interactive Tours</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {tours.map((tour) => {
          const completed = isTourCompleted(tour.id);

          return (
            <DropdownMenuItem
              key={tour.id}
              onClick={() => startTour(tour.id)}
              className="flex items-start gap-3 py-2 cursor-pointer"
            >
              <div className="shrink-0 mt-0.5">
                {completed ? (
                  <CheckCircle2 className="h-4 w-4 text-success-500" />
                ) : (
                  <Play className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{tour.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-1">{tour.description}</p>
              </div>
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={resetOnboarding}
          className="flex items-center gap-2 text-muted-foreground cursor-pointer"
        >
          <RotateCcw className="h-4 w-4" />
          Restart Onboarding
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <a
            href="https://help.billbee.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 cursor-pointer"
          >
            <ExternalLink className="h-4 w-4" />
            Support documents
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
